import React, {
  useContext,
  useEffect,
  useState,
} from "react";

import "./ProductDisplay.css";
import star_icon from "../Assets/star_icon.png";
import { useCartStore } from "../../stores/useCartStores";
import { useUserStore } from "../../stores/useUserStores";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ShopContext } from "../../Context/ShopContext";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:5000/api"
).replace(/\/api\/?$/, "");

const resolveImage = (imagePath) => {
  if (!imagePath) {
    return "/images/placeholder.png";
  }

  return imagePath.startsWith("http")
    ? imagePath
    : `${API_BASE_URL}${imagePath}`;
};

const ProductDisplay = ({ product }) => {
  const { addToCart } = useCartStore();
  const { user } = useUserStore();

  const {
    addToWishlist,
    removeFromWishlist,
    wishlist = [],
  } = useContext(ShopContext);

  const navigate = useNavigate();
  const location = useLocation();

  const productImages =
    product?.product_images ||
    product?.images ||
    [];

  const firstImage =
    productImages[0]?.image_url ||
    product?.image ||
    "";

  const [mainImage, setMainImage] =
    useState(firstImage);

  const [selectedSize, setSelectedSize] =
    useState("");

  const [quantity, setQuantity] =
    useState(1);

  const [
    wishlistMessage,
    setWishlistMessage,
  ] = useState("");

  const [
    floatingMessage,
    setFloatingMessage,
  ] = useState("");

  const availableQuantity = Number(
    product?.available_quantity || 0
  );

  const isOutOfStock =
    availableQuantity <= 0;

  const maximumOrderQuantity = Math.min(
    10,
    Math.max(0, availableQuantity)
  );

  useEffect(() => {
    setMainImage(firstImage);
    setQuantity(1);
  }, [firstImage, product?.id]);

  const isWishlisted = wishlist.some(
    (item) =>
      Number(item.id) === Number(product?.id)
  );

  const showFloatingMessage = (message) => {
    setFloatingMessage(message);

    setTimeout(() => {
      setFloatingMessage("");
    }, 2000);
  };

  const handleAddToCart = async () => {
    if (isOutOfStock) {
      showFloatingMessage(
        "This item is currently out of stock."
      );

      return;
    }

    if (quantity > availableQuantity) {
      showFloatingMessage(
        `Only ${availableQuantity} unit${
          availableQuantity === 1
            ? " is"
            : "s are"
        } available.`
      );

      return;
    }

    if (!user) {
      showFloatingMessage(
        "You must be logged in to add items to the cart."
      );

      navigate("/login", {
        state: {
          from: location,
          action: {
            type: "addToCart",
            productId: product.id,
            quantity,
            size: selectedSize,
          },
        },
      });

      return;
    }

    if (!selectedSize) {
      showFloatingMessage(
        "Please select a size."
      );

      return;
    }

    try {
      await addToCart(
        product.id,
        quantity,
        selectedSize
      );

      showFloatingMessage(
        "Item added to cart successfully!"
      );
    } catch (error) {
      console.error(
        "Failed to add item to cart:",
        error
      );

      showFloatingMessage(
        error.response?.data?.message ||
          "Failed to add item to cart."
      );
    }
  };

  const handleWishlist = () => {
    if (!selectedSize) {
      showFloatingMessage(
        "Please select a size before adding to wishlist."
      );

      return;
    }

    if (isWishlisted) {
      removeFromWishlist(product.id);

      setWishlistMessage(
        "The item has been removed from favourites."
      );
    } else {
      addToWishlist(
        product,
        selectedSize,
        quantity,
        mainImage
      );

      setWishlistMessage(
        "The item has been added to favourites."
      );
    }

    setTimeout(() => {
      setWishlistMessage("");
    }, 3000);
  };

  if (!product) {
    return (
      <div className="productdisplay">
        <p>Product not found.</p>
      </div>
    );
  }

  return (
    <div className="productdisplay">
      <div className="productdisplay-left">
        <div className="productdisplay-img-list">
          {productImages.map(
            (image, index) => (
              <img
                key={
                  image.id ||
                  image.image_url ||
                  index
                }
                src={resolveImage(
                  image.image_url
                )}
                alt={`${product.name} ${index + 1}`}
                onClick={() =>
                  setMainImage(
                    image.image_url
                  )
                }
                className={
                  mainImage === image.image_url
                    ? "active"
                    : ""
                }
                loading="lazy"
                decoding="async"
              />
            )
          )}
        </div>

        <div className="productdisplay-image">
          <img
            className="productdisplay-main-img"
            src={resolveImage(mainImage)}
            alt={product.name}
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>

      <div className="productdisplay-right">
        <h1>{product.name}</h1>

        <div className="productdisplay-right-stars">
          {[...Array(5)].map((_, index) => (
            <img
              key={index}
              src={star_icon}
              alt="star"
            />
          ))}
        </div>

        <div className="productdisplay-right-prices">
          <div className="productdisplay-right-price-new">
            ₦{product.price}
          </div>
        </div>

        {isOutOfStock ? (
  <p className="product-stock-status out">
    Out of stock
  </p>
) : (
  availableQuantity <= 2 && (
    <p className="product-stock-status low">
      Hurry! Only {availableQuantity}{" "}
      {availableQuantity === 1 ? "item" : "items"} left.
    </p>
  )
)}

        <div className="productdisplay-right-description">
          {product.description}
        </div>

        <div className="productdisplay-right-size">
          <h1>Select size</h1>

          <div className="productdisplay-right-sizes">
            {[
              6,
              8,
              10,
              12,
              14,
              16,
              18,
              20,
              22,
            ].map((size) => (
              <div
                key={size}
                onClick={() =>
                  setSelectedSize(size)
                }
                className={`size-box ${
                  selectedSize === size
                    ? "active"
                    : ""
                }`}
              >
                {size}
              </div>
            ))}
          </div>

          <p className="productdisplay-right-category">
            <span>Note:</span> Not sure of
            your size? Scroll down to check
            the size chart for accuracy.
          </p>

          {!isOutOfStock && (
            <div className="quantity-selector">
              <label htmlFor="quantity">
                Quantity :
              </label>

              <select
                id="quantity"
                value={quantity}
                onChange={(event) =>
                  setQuantity(
                    Number(
                      event.target.value
                    )
                  )
                }
              >
                {Array.from(
                  {
                    length:
                      maximumOrderQuantity,
                  },
                  (_, index) =>
                    index + 1
                ).map((number) => (
                  <option
                    key={number}
                    value={number}
                  >
                    {number}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="productdisplay-right-prices">
            <div className="productdisplay-right-price-new">
              Color: {product.color}
            </div>
          </div>

          <button
            type="button"
            className={`wishlist-btn ${
              isWishlisted
                ? "black-heart"
                : ""
            }`}
            onClick={handleWishlist}
          >
            {isWishlisted
              ? "🖤 Wishlisted"
              : "🤍 Add to Wishlist"}
          </button>

          <button
            type="button"
            onClick={handleAddToCart}
            disabled={isOutOfStock}
          >
            {isOutOfStock
              ? "OUT OF STOCK"
              : "ADD TO CART"}
          </button>

          <p className="productdisplay-right-category">
            <span>Category:</span>{" "}
            {product.category}
          </p>

          <p className="productdisplay-right-category">
            <span>Tags:</span>{" "}
            {product.section_category},{" "}
            {product.fabric_type}
          </p>
        </div>

        {wishlistMessage && (
          <div className="wishlist-message">
            {wishlistMessage}
          </div>
        )}

        {floatingMessage && (
          <div className="floating-message">
            {floatingMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDisplay;